import {Button, CircularProgress} from "@material-ui/core";
import React, {useState, useEffect} from "react";
import Grid from "@material-ui/core/Grid";
import {statesOptions} from "../../../constants/constantVariables";
import DropDownPicker from "../../forms/ReactHookForm/DropDown/DropDownPicker";
import {FormProvider, useForm} from "react-hook-form";
import CheckBox from 'components/forms/Checkbox'
import TypoGraph from "../../forms/typograph";
import TextFieldForm from "../../forms/TextFieldForm";
import "./SelfRegister.css"
import logo from "../../../styles/images/logo.svg";
import request from "../../../tools/request";
import {Toast} from "../../shared/Toaster";
import {formatPhoneNumber} from "../../../tools/helper";
import {trackSignupConversion} from "../../../analytics/googleAds";
import {captureSignupCompleted} from "../../../posthog/posthogConfig";
import {
    validateEmail,
    validatePassword,
    validatePasswordMatch
} from "../../../tools/forms/validators";

const validatePhone = (value) => {
    if (!value) return true;
    const digits = value.replace(/\D/g, '');
    if (digits.length < 10) return 'Phone must be at least 10 digits';
    return true;
};

const validateZipCode = (value) => {
    if (!value) return true;
    if (/\D/.test(value)) return 'Zip Code must contain only numbers';
    if (value.length !== 5) return 'Zip Code must be exactly 5 digits';
    return true;
};

const parseName = (fullName) => {
    const parts = fullName.trim().split(/\s+/);
    return {
        firstName: parts[0] || "",
        lastName: parts.slice(1).join(" ") || "",
    };
};

const SelfRegister = ({redirectToLogin, setView}) => {
    const methods = useForm();
    const [loading, setLoading] = useState(false)
    const [checked, setChecked] = useState(false)
    const { handleSubmit, formState } = methods
    const { errors } = formState

    const phoneValue = methods.watch('phone');

    useEffect(() => {
        if (phoneValue) {
            const formatted = formatPhoneNumber(phoneValue);
            if (formatted !== phoneValue) {
                methods.setValue('phone', formatted);
            }
        }
    }, [phoneValue]);

    const zipValue = methods.watch('zipCode');

    useEffect(() => {
        if (zipValue) {
            const digitsOnly = zipValue.replace(/\D/g, '');
            if (digitsOnly !== zipValue) {
                methods.setValue('zipCode', digitsOnly);
            }
        }
    }, [zipValue]);

    const registerUser = (data) => {
        setLoading(true)

        const { firstName, lastName } = parseName(data.username);
        const body = {
            firstName,
            lastName,
            companyName: data.companyName.trim(),
            email: data.email.trim(),
            phone: data.phone.trim(),
            address1: data.address1.trim(),
            address2: data.address2 ? data.address2.trim() : "",
            city: data.city.trim(),
            state: data.state.trim(),
            zipCode: data.zipCode.trim(),
            password: data.password.trim(),
            confirmedPassword: data.confirmedPassword.trim(),
        }

        request("self-register", "POST", body)
            .then(() => {
                trackSignupConversion()
                captureSignupCompleted('self_register')
                Toast({
                    type: "success",
                    message: "Please confirm your email"
                })
                setView("selfRegisterSuccessful")
            })
            .finally(() => setLoading(false))
    }


    return (
        <Grid item xs={12} sm={6} md={6} lg={6} className="gs-form-col" style={{zIndex: 1}}>
            <FormProvider {...methods}>
                <form onSubmit={handleSubmit(registerUser)} noValidate name="form" className="self-register-container gs-card">

                    <img className="gs-form-logo" src={logo} alt="ROX" />

                    <div className="gs-card-head">
                        <TypoGraph variant="h4" content={"Create your account"} className={"gs-title"} />
                        <TypoGraph variant="body1" content={"Your first three applications are on us. No card required."} className={"gs-subtitle"} />
                    </div>

                    <div className="gs-field">
                        <TypoGraph variant="body1" content={"Full name"} className={"gs-label"}>Full name <span className="gs-required">*</span></TypoGraph>
                        <TextFieldForm
                            fullWidth
                            id="username"
                            name="username"
                            placeholder="Jane Doe"
                            variant="outlined"
                            size="small"
                            required
                            className={"gs-input-field"}
                        />
                    </div>

                    <div className="gs-field">
                        <TypoGraph variant="body1" content={"Work email"} className={"gs-label"}>Work email <span className="gs-required">*</span></TypoGraph>
                        <TextFieldForm
                            fullWidth
                            id="email"
                            required
                            name="email"
                            placeholder="jane@company.com"
                            variant="outlined"
                            validate={validateEmail}
                            size="small"
                            className={"gs-input-field"}
                        />
                    </div>

                    <div className="gs-field">
                        <TypoGraph variant="body1" content={"Phone"} className={"gs-label"}>Phone <span className="gs-required">*</span></TypoGraph>
                        <TextFieldForm
                            fullWidth
                            id="phone"
                            name="phone"
                            placeholder="555-123-4567"
                            variant="outlined"
                            size="small"
                            required
                            className={"gs-input-field"}
                            validate={validatePhone}
                            maxLength={{ value: 12, message: 'Phone must be no more than 12 characters' }}
                            InputProps={{ inputProps: { maxLength: 12 } }}
                        />
                    </div>

                    <div className="gs-field">
                        <TypoGraph variant="body1" content={"Company name"} className={"gs-label"}>Company name <span className="gs-required">*</span></TypoGraph>
                        <TextFieldForm
                            fullWidth
                            id="companyName"
                            name="companyName"
                            placeholder="Company, Inc."
                            className={"gs-input-field"}
                            variant="outlined"
                            size="small"
                            required
                            minLength={{ value: 4, message: 'Company name must be at least 4 characters' }}
                        />
                    </div>

                    <div className="gs-field">
                        <TypoGraph variant="body1" content={"Password"} className={"gs-label"}>Password <span className="gs-required">*</span></TypoGraph>
                        <TextFieldForm
                            fullWidth
                            id="password"
                            name="password"
                            placeholder="Create a password"
                            validate={validatePassword}
                            type="password"
                            className={"gs-input-field"}
                            variant="outlined"
                            size="small"
                            required
                            showPasswordIcon
                        />
                    </div>

                    <div className="gs-field">
                        <TypoGraph variant="body1" content={"Confirm password"} className={"gs-label"}>Confirm password <span className="gs-required">*</span></TypoGraph>
                        <TextFieldForm
                            fullWidth
                            id="confirmedPassword"
                            type="password"
                            showPasswordIcon
                            name="confirmedPassword"
                            placeholder="Re-enter your password"
                            className={"gs-input-field"}
                            validate={validatePasswordMatch}
                            variant="outlined"
                            size="small"
                            required
                        />
                    </div>

                    <div className="gs-field">
                        <TypoGraph variant="body1" content={"Address 1"} className={"gs-label"}>Address 1 <span className="gs-required">*</span></TypoGraph>
                        <TextFieldForm
                            fullWidth
                            id="address1"
                            name="address1"
                            placeholder="123 Main St"
                            variant="outlined"
                            size="small"
                            required
                            className={"gs-input-field"}
                            minLength={{ value: 2, message: 'Address must be at least 2 characters' }}
                        />
                    </div>

                    <div className="gs-field">
                        <TypoGraph variant="body1" content={"Address 2 (optional)"} className={"gs-label"} />
                        <TextFieldForm
                            fullWidth
                            id="address2"
                            name="address2"
                            placeholder="Suite 100"
                            variant="outlined"
                            size="small"
                            className={"gs-input-field"}
                        />
                    </div>

                    <div className="gs-field gs-row">
                        <div>
                            <TypoGraph variant="body1" content={"City"} className={"gs-label"}>City <span className="gs-required">*</span></TypoGraph>
                            <TextFieldForm
                                fullWidth
                                id="city"
                                name="city"
                                placeholder="City"
                                variant="outlined"
                                size="small"
                                className={"gs-input-field"}
                                required
                                minLength={{ value: 3, message: 'City must be at least 3 characters' }}
                            />
                        </div>
                        <div>
                            <TypoGraph variant="body1" content={"State"} className={"gs-label"}>State <span className="gs-required">*</span></TypoGraph>
                            <DropDownPicker
                                options={statesOptions['USA']}
                                label={""}
                                name={'state'}
                                labelField={'label'}
                                valueField={'value'}
                                error={!!errors?.state}
                                helperText={errors?.state?.message}
                                preSelectedValue={""}
                                fullWidth
                            />
                        </div>
                        <div>
                            <TypoGraph variant="body1" content={"Zip Code"} className={"gs-label"}>Zip Code <span className="gs-required">*</span></TypoGraph>
                            <TextFieldForm
                                fullWidth
                                id="zipCode"
                                name="zipCode"
                                placeholder="12345"
                                variant="outlined"
                                size="small"
                                className={"gs-input-field"}
                                required
                                validate={validateZipCode}
                                InputProps={{ inputProps: { maxLength: 5 } }}
                            />
                        </div>
                    </div>

                    <CheckBox
                        className="check-box-style"
                        checked={checked}
                        required
                        onChange={(e) => setChecked(e)}
                        label={
                            <>
                                I agree to the{" "}
                                <a href="https://www.roxwrite.com/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Use</a> and{" "}
                                <a href="https://www.roxwrite.com/privacy-policy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                            </>
                        }
                    />

                    <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        className="gs-cta"
                        disabled={loading || !checked}
                        type="submit"
                    >
                        {loading ? <CircularProgress size={25} color="inherit" /> : "Create account"}
                    </Button>

                    <Grid container justifyContent="center" spacing={2} style={{ marginTop: "1rem" }}>
                        <TypoGraph
                            variant="body1"
                            content="Already have an account?"
                            className="mr-1"
                        />
                        <TypoGraph
                            variant="body2"
                            content="Sign in"
                            className="sign-up-button"
                            onClick={redirectToLogin}
                        />
                    </Grid>
                </form>
            </FormProvider>
        </Grid>
    );
};

export default SelfRegister;